var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _activeQuantityRequest, _debouncedOnChange, _pendingCartFetch, _CartItemsComponent_instances, isExternalCartUpdate_fn, _handleDiscountUpdate, _handleNoteUpdate, onQuantityChange_fn, _handleCartError, _handleCartUpdate, disableCartItems_fn, enableCartItems_fn, updateQuantitySelectors_fn, updateCartQuantitySelectorButtonStates_fn;
import { Component } from "@theme/component";
import {
  fetchConfig,
  setCartCheckout,
  fetchCartJson,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition
} from "@theme/utilities";
import { trackCartRequest, renderCartSection, applyCurrentCartItems } from "@theme/section-renderer";
import { ThemeEvents, QuantitySelectorUpdateEvent } from "@theme/events";
import { cartPerformance } from "@theme/performance";
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents
} from "@shopify/events";
class CartItemsComponent extends createViewEventElement(Component) {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartItemsComponent_instances);
    this.pendingQuantityChanges = /* @__PURE__ */ new Map();
    this.quantityQueueRunning = false;
    this.quantityQueueEpoch = 0;
    this.quantityQueueDetached = false;
    this.quantityUpdateFailed = false;
    this.cartRecoveryPending = false;
    this.cartRenderControllers = /* @__PURE__ */ new Set();
    this.cartEventOperations = /* @__PURE__ */ new Set();
    this.cartEventCheckoutOwner = {};
    this.cartEventGeneration = 0;
    this.cartEventFailure = 0;
    this.cartEventSuccess = 0;
    this.handlePageShow = (event) => {
      const entry = performance.getEntriesByType("navigation")[0];
      if (!event.persisted && (!entry || entry.type !== "back_forward")) return;
      if (!this.dataset.sectionId || this.quantityQueueDetached || this.cartRecoveryPending) return;
      this.cartRecoveryPending = true;
      this.syncQuantityCheckout();
      void this.recoverInterruptedUpdate();
    };
    this.handleQuantityEvent = (event) => {
      if (event instanceof QuantitySelectorUpdateEvent && event.target instanceof Node && this.contains(event.target)) {
        __privateMethod(this, _CartItemsComponent_instances, onQuantityChange_fn).call(this, event);
      }
    };
    __privateAdd(this, _debouncedOnChange, debounce(
      () => this.flushQuantityChanges(),
      300
    ));
    __privateAdd(this, _pendingCartFetch, null);
    __privateAdd(this, _activeQuantityRequest, null);
    __privateAdd(this, _handleDiscountUpdate, (event) => {
      const ticket = trackCartRequest(event.promise);
      const epoch = this.quantityQueueEpoch;
      var _a;
      const external = __privateMethod(this, _CartItemsComponent_instances, isExternalCartUpdate_fn).call(this, event);
      return this.observeCartEvent(event, async ({ detail }, signal) => {
        if (signal.aborted || this.quantityQueueDetached || this.quantityQueueEpoch !== epoch) return;
        var _a2;
        const sectionsHtml = (_a2 = detail == null ? void 0 : detail.sections) == null ? void 0 : _a2[this.sectionId];
        if (sectionsHtml) {
          await this.renderCartUpdate(sectionsHtml, ticket, { mode: this.isDrawer ? "hydration" : "full" }, epoch, signal);
          if (!signal.aborted && !this.quantityQueueDetached && this.quantityQueueEpoch === epoch) __privateMethod(this, _CartItemsComponent_instances, updateCartQuantitySelectorButtonStates_fn).call(this);
        } else if (external) {
          await this.renderCartUpdate(null, ticket, {
            mode: this.isDrawer ? "hydration" : "full"
          }, epoch, signal);
          if (!signal.aborted && !this.quantityQueueDetached && this.quantityQueueEpoch === epoch) __privateMethod(this, _CartItemsComponent_instances, updateCartQuantitySelectorButtonStates_fn).call(this);
        }
        return Boolean(sectionsHtml || external);
      });
    });
    __privateAdd(this, _handleNoteUpdate, (event) => {
      const ticket = trackCartRequest(event.promise);
      const epoch = this.quantityQueueEpoch;
      var _a;
      if (!__privateMethod(this, _CartItemsComponent_instances, isExternalCartUpdate_fn).call(this, event)) return;
      return this.observeCartEvent(event, async ({ detail }, signal) => {
        if (signal.aborted || this.quantityQueueDetached || this.quantityQueueEpoch !== epoch) return;
        const sections = (
          /** @type {Record<string, string> | undefined} */
          detail == null ? void 0 : detail.sections
        );
        const sectionsHtml = sections == null ? void 0 : sections[this.sectionId];
        if (sectionsHtml) {
          await this.renderCartUpdate(sectionsHtml, ticket, { mode: this.isDrawer ? "hydration" : "full" }, epoch, signal);
        } else {
          await this.renderCartUpdate(null, ticket, {
            mode: this.isDrawer ? "hydration" : "full"
          }, epoch, signal);
        }
        if (!signal.aborted && !this.quantityQueueDetached && this.quantityQueueEpoch === epoch) __privateMethod(this, _CartItemsComponent_instances, updateCartQuantitySelectorButtonStates_fn).call(this);
        return true;
      });
    });
    __privateAdd(this, _handleCartError, (line, parsedResponseText) => {
      const quantitySelector = (this.refs.quantitySelectors || [])[line - 1];
      const quantityInput = quantitySelector == null ? void 0 : quantitySelector.querySelector("input");
      if (quantityInput) quantityInput.value = quantityInput.defaultValue;
      const cartItemError = this.refs["cartItemError-".concat(line)];
      const cartItemErrorContainer = this.refs["cartItemErrorContainer-".concat(line)];
      if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
        cartItemError.textContent = parsedResponseText.errors;
        cartItemErrorContainer.classList.remove("hidden");
      } else {
        const notice = this.querySelector("[data-cart-update-error]");
        if (notice) notice.hidden = false;
      }
      this.dispatchEvent(
        new CartErrorEvent({
          error: parsedResponseText.errors || "Cart update failed",
          code: "INVALID"
        })
      );
    });
    __privateAdd(this, _handleCartUpdate, (event) => {
      const ticket = trackCartRequest(event.promise);
      const epoch = this.quantityQueueEpoch;
      var _a;
      if (event.target === this) return;
      return this.observeCartEvent(event, async ({ detail }, signal) => {
        if (signal.aborted || this.quantityQueueDetached || this.quantityQueueEpoch !== epoch) return;
        var _a2, _b, _c;
        const renderTicket = detail && detail.source === "pb-card-add" ? null : ticket;
        const sections = detail == null ? void 0 : detail.sections;
        const cartItemsHtml = sections == null ? void 0 : sections[this.sectionId];
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector("[data-cart-drawer-empty]") !== null;
        const mode = this.isDrawer ? "hydration" : "full";
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer
        };
        if (cartItemsHtml) {
          const existingKeys = new Set((_b = (_a2 = this.refs.cartItemRows) == null ? void 0 : _a2.map((row) => row.dataset.key)) != null ? _b : []);
          if (wasEmptyCartDrawer) {
            await startViewTransition(() => this.renderCartUpdate(cartItemsHtml, renderTicket, morphOptions, epoch, signal), ["fill-cart-drawer"]);
          } else {
            await this.renderCartUpdate(cartItemsHtml, renderTicket, morphOptions, epoch, signal);
          }
          if (signal.aborted || this.quantityQueueDetached || this.quantityQueueEpoch !== epoch) return;
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of (_c = this.refs.cartItemRows) != null ? _c : []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add("adding");
                onAnimationEnd(row, () => row.classList.remove("adding"));
              }
            }
          }
          if (!signal.aborted && !this.quantityQueueDetached && this.quantityQueueEpoch === epoch) __privateMethod(this, _CartItemsComponent_instances, updateCartQuantitySelectorButtonStates_fn).call(this);
        } else {
          await this.renderCartUpdate(null, renderTicket, morphOptions, epoch, signal);
          if (!signal.aborted && !this.quantityQueueDetached && this.quantityQueueEpoch === epoch) __privateMethod(this, _CartItemsComponent_instances, updateCartQuantitySelectorButtonStates_fn).call(this);
        }
        return true;
      });
    });
  }
  syncCartEventCheckout() {
    const pending = this.cartEventOperations.size > 0;
    const failed = this.cartEventFailure > this.cartEventSuccess;
    setCartCheckout(this.cartEventCheckoutOwner, this.quantityQueueDetached ? null : pending ? "pending" : failed ? "failed" : null);
  }
  observeCartEvent(event, apply) {
    if (!event.promise || typeof event.promise.then !== "function" || this.quantityQueueDetached) return Promise.resolve(false);
    const generation = ++this.cartEventGeneration;
    const epoch = this.quantityQueueEpoch;
    const controller = new AbortController();
    this.cartEventOperations.add(controller);
    this.syncCartEventCheckout();
    const active = () => !controller.signal.aborted && !this.quantityQueueDetached && this.quantityQueueEpoch === epoch;
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        controller.signal.removeEventListener("abort", onAbort);
        callback(value);
      };
      const onAbort = () => {
        const error = new Error("Cart event cancelled");
        error.name = "AbortError";
        finish(reject, error);
      };
      const timer = setTimeout(() => {
        const error = new Error("Cart event update timed out");
        error.name = "TimeoutError";
        finish(reject, error);
        controller.abort();
      }, 6e4);
      controller.signal.addEventListener("abort", onAbort, { once: true });
      Promise.resolve(event.promise).then(async (data) => {
        var _a;
        if (!active()) return false;
        if (!data || ((_a = data.detail) == null ? void 0 : _a.didError)) throw new Error("Cart event could not be confirmed");
        return await apply(data, controller.signal);
      }).then((result) => finish(resolve, result), (error) => finish(reject, error));
    }).then((rendered) => {
      if (active() && rendered) {
        this.cartEventSuccess = Math.max(this.cartEventSuccess, generation);
        const notice = this.querySelector("[data-cart-update-error]");
        if (notice && this.cartEventSuccess >= this.cartEventFailure && !this.quantityUpdateFailed) notice.hidden = true;
      }
      return Boolean(rendered);
    }).catch((error) => {
      if (!this.quantityQueueDetached && this.quantityQueueEpoch === epoch) {
        this.cartEventFailure = Math.max(this.cartEventFailure, generation);
        const notice = this.querySelector("[data-cart-update-error]");
        if (notice && this.cartEventFailure > this.cartEventSuccess) notice.hidden = false;
        if ((error == null ? void 0 : error.name) !== "AbortError") console.warn("[cart-items] Event promise rejected:", error);
      }
      return false;
    }).finally(() => {
      this.cartEventOperations.delete(controller);
      this.syncCartEventCheckout();
    });
  }
  async renderCartUpdate(html, ticket, options, epoch = this.quantityQueueEpoch, parentSignal) {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    if (parentSignal) parentSignal.addEventListener("abort", cancel, { once: true });
    if ((parentSignal == null ? void 0 : parentSignal.aborted) || this.quantityQueueDetached || this.quantityQueueEpoch !== epoch) controller.abort();
    this.cartRenderControllers.add(controller);
    try {
      return await renderCartSection(this.sectionId, html, { ...options, signal: controller.signal }, ticket);
    } catch (error) {
      if (!parentSignal && error && error.name !== "AbortError" && !this.quantityQueueDetached && this.quantityQueueEpoch === epoch) {
        const notice = this.querySelector("[data-cart-update-error]");
        if (notice) notice.hidden = false;
      }
      throw error;
    } finally {
      if (parentSignal) parentSignal.removeEventListener("abort", cancel);
      this.cartRenderControllers.delete(controller);
    }
  }
  connectedCallback() {
    super.connectedCallback();
    this.quantityQueueDetached = false;
    // Translation apps can reconnect a clone of a busy cart without its request.
    const needsRecovery = this.classList.contains("cart-items-disabled");
    this.cartRecoveryPending = needsRecovery;
    this.syncQuantityCheckout();
    this.syncCartEventCheckout();
    document.addEventListener(StandardEvents.cartLinesUpdate, __privateGet(this, _handleCartUpdate));
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantityEvent);
    document.addEventListener(StandardEvents.cartDiscountUpdate, __privateGet(this, _handleDiscountUpdate));
    document.addEventListener(StandardEvents.cartNoteUpdate, __privateGet(this, _handleNoteUpdate));
    window.addEventListener("pageshow", this.handlePageShow);
    if (needsRecovery) void this.recoverInterruptedUpdate();
  }
  async recoverInterruptedUpdate() {
    const epoch = this.quantityQueueEpoch;
    let confirmed = false;
    try {
      // Read the current server cart; never replay the interrupted quantity POST.
      await this.renderCartUpdate(null, null, {
        mode: this.isDrawer ? "hydration" : "full",
        requiredSelector: "cart-items-component",
        injectStylesheet: this.isDrawer && this.querySelector("[data-cart-drawer-empty]") !== null
      }, epoch);
      if (this.quantityQueueDetached || this.quantityQueueEpoch !== epoch) return;
      __privateMethod(this, _CartItemsComponent_instances, updateCartQuantitySelectorButtonStates_fn).call(this);
      confirmed = true;
      this.dispatchEvent(new Event("cart-section:restored", { bubbles: true }));
    } catch (error) {
      if (!this.quantityQueueDetached && this.quantityQueueEpoch === epoch && (error == null ? void 0 : error.name) !== "AbortError") {
        console.warn("[cart-items] Interrupted update recovery failed:", error);
      }
    } finally {
      if (!this.quantityQueueDetached && this.quantityQueueEpoch === epoch) {
        this.cartRecoveryPending = false;
        this.quantityUpdateFailed = !confirmed;
        const notice = this.querySelector("[data-cart-update-error]");
        if (notice) notice.hidden = confirmed;
        resetShimmer(this);
        __privateMethod(this, _CartItemsComponent_instances, enableCartItems_fn).call(this);
        this.syncQuantityCheckout();
      }
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("pageshow", this.handlePageShow);
    this.quantityQueueDetached = true;
    this.quantityQueueEpoch++;
    if (this.cartEventOperations.size) this.cartEventFailure = ++this.cartEventGeneration;
    for (const controller of this.cartEventOperations) controller.abort();
    this.cartEventOperations.clear();
    this.syncCartEventCheckout();
    for (const controller of this.cartRenderControllers) controller.abort();
    this.cartRenderControllers.clear();
    this.pendingQuantityChanges.clear();
    setCartCheckout(this, null);
    __privateGet(this, _debouncedOnChange).cancel();
    const active = __privateGet(this, _activeQuantityRequest);
    if (active) active.abort();
    document.removeEventListener(StandardEvents.cartLinesUpdate, __privateGet(this, _handleCartUpdate));
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantityEvent);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, __privateGet(this, _handleDiscountUpdate));
    document.removeEventListener(StandardEvents.cartNoteUpdate, __privateGet(this, _handleNoteUpdate));
  }
  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    return this.queueQuantityChange({
      line,
      quantity: 0,
      action: "clear"
    }, true);
  }
  queueQuantityChange(config, immediate = false) {
    if (this.quantityQueueDetached || !Number.isInteger(config.quantity) || config.quantity < 0) return;
    const row = (this.refs.cartItemRows || [])[config.line - 1];
    if (!row || !row.dataset.key) return;
    const lineId = row.dataset.key;
    this.pendingQuantityChanges.set(lineId, { ...config, lineId });
    this.quantityUpdateFailed = false;
    this.syncQuantityCheckout();
    if (immediate) {
      __privateGet(this, _debouncedOnChange).cancel();
      return this.flushQuantityChanges();
    }
    __privateGet(this, _debouncedOnChange)();
  }
  syncQuantityCheckout() {
    const pending = this.cartRecoveryPending || this.pendingQuantityChanges.size || this.quantityQueueRunning || __privateGet(this, _activeQuantityRequest);
    setCartCheckout(this, this.quantityQueueDetached ? null : pending ? "pending" : this.quantityUpdateFailed ? "failed" : null);
  }
  async flushQuantityChanges() {
    if (this.quantityQueueRunning || this.quantityQueueDetached || this.classList.contains("cart-items-disabled")) return;
    this.quantityQueueRunning = true;
    const epoch = this.quantityQueueEpoch;
    try {
      while (this.pendingQuantityChanges.size && !this.quantityQueueDetached && this.quantityQueueEpoch === epoch) {
        const [lineId, config] = this.pendingQuantityChanges.entries().next().value;
        this.pendingQuantityChanges.delete(lineId);
        const index = (this.refs.cartItemRows || []).findIndex((row) => row.dataset.key === lineId);
        if (index < 0) continue;
        const confirmed = await this.updateQuantity({ ...config, line: index + 1 });
        if (!confirmed) {
          if (this.quantityQueueEpoch === epoch) {
            if (this.pendingQuantityChanges.size) {
              const notice = this.querySelector("[data-cart-update-error]");
              if (notice) notice.hidden = false;
            }
            this.pendingQuantityChanges.clear();
          }
          break;
        }
      }
    } catch (error) {
      if (this.quantityQueueEpoch === epoch && !this.quantityQueueDetached) {
        this.pendingQuantityChanges.clear();
        this.quantityUpdateFailed = true;
        const notice = this.querySelector("[data-cart-update-error]");
        if (notice) notice.hidden = false;
        console.error(error);
      }
    } finally {
      this.quantityQueueRunning = false;
      this.syncQuantityCheckout();
      if (!this.quantityQueueDetached && this.pendingQuantityChanges.size && this.quantityQueueEpoch !== epoch) {
        __privateGet(this, _debouncedOnChange)();
      }
    }
  }
  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (this.quantityQueueDetached || this.classList.contains("cart-items-disabled")) return;
    if (!Number.isInteger(config.quantity) || config.quantity < 0) return;
    var _a, _b;
    const lineId = (_b = (_a = (this.refs.cartItemRows || [])[config.line - 1]) == null ? void 0 : _a.dataset.key) != null ? _b : "";
    if (!lineId || config.lineId && config.lineId !== lineId) return;
    const errorNotice = this.querySelector("[data-cart-update-error]");
    if (errorNotice) errorNotice.hidden = true;
    const controller = new AbortController();
    __privateSet(this, _activeQuantityRequest, controller);
    const requestEpoch = this.quantityQueueEpoch;
    this.quantityUpdateFailed = false;
    this.syncQuantityCheckout();
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker("".concat(config.action, ":user-action"));
    __privateMethod(this, _CartItemsComponent_instances, disableCartItems_fn).call(this);
    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const sectionsToUpdate = /* @__PURE__ */ new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    const body = JSON.stringify({
      id: lineId,
      quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname
    });
    cartTotal == null ? void 0 : cartTotal.shimmer();
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const ticket = trackCartRequest(deferredUpdatePromise.promise);
    let updateConfirmed = false;
    let staleLine = false;
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === "change" && quantity > 0 ? "update" : "remove",
        context: "cart",
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise
      })
    );
    return fetchCartJson(Theme.routes.cart_change_url, fetchConfig("json", { body }), controller, 3e4).then(async (parsedResponseText) => {
      if (controller.signal.aborted) {
        const cancelled = new Error("Cart request cancelled");
        cancelled.name = "AbortError";
        throw cancelled;
      }
      if (!parsedResponseText || typeof parsedResponseText !== "object") throw new Error("Invalid cart response");
      if (parsedResponseText.errors) {
        __privateGet(this, _handleCartError).call(this, line, parsedResponseText);
        deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
        return false;
      }
      if (!Array.isArray(parsedResponseText.items) || !Number.isInteger(parsedResponseText.item_count)) throw new Error("Invalid cart response");
      const newCartItemCount = parsedResponseText.item_count;
      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: parsedResponseText.sections,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: "cart-items-component",
          didError: false
        }
      });
      await applyCurrentCartItems(parsedResponseText.items, ticket, controller, (items) => {
        __privateMethod(this, _CartItemsComponent_instances, updateQuantitySelectors_fn).call(this, { items });
      });
      const sectionHTML = parsedResponseText.sections && parsedResponseText.sections[this.sectionId];
      await this.renderCartUpdate(sectionHTML, ticket, { mode: this.isDrawer ? "hydration" : "full" }, requestEpoch);
      __privateMethod(this, _CartItemsComponent_instances, updateCartQuantitySelectorButtonStates_fn).call(this);
      updateConfirmed = true;
      return true;
    }).catch((error) => {
      deferredUpdatePromise.reject(error);
      if (error && error.name === "AbortError") return false;
      if (error && (error.status === 400 || error.status === 422) && error.data) {
        const message = error.data.description || error.data.message || error.data.errors;
        if (typeof message === "string") {
          staleLine = error.status === 400 && /no valid id or line parameter/i.test(message);
          __privateGet(this, _handleCartError).call(this, line, { errors: message });
          return false;
        }
      }
      console.error(error);
      const notice = this.querySelector("[data-cart-update-error]");
      if (notice) notice.hidden = false;
      this.dispatchEvent(
        new CartErrorEvent({
          error: (error == null ? void 0 : error.message) || "Failed to update cart",
          code: "SERVICE_UNAVAILABLE"
        })
      );
      return false;
    }).finally(async () => {
      if (__privateGet(this, _activeQuantityRequest) === controller) __privateSet(this, _activeQuantityRequest, null);
      if (this.quantityQueueEpoch === requestEpoch) this.quantityUpdateFailed = !updateConfirmed;
      resetShimmer(this);
      __privateMethod(this, _CartItemsComponent_instances, enableCartItems_fn).call(this);
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      if (!this.quantityQueueRunning && !this.quantityQueueDetached && this.pendingQuantityChanges.size) {
        if (updateConfirmed) __privateGet(this, _debouncedOnChange)();
        else {
          this.pendingQuantityChanges.clear();
          const notice = this.querySelector("[data-cart-update-error]");
          if (notice) notice.hidden = false;
        }
      }
      this.syncQuantityCheckout();
      if (staleLine && !this.quantityQueueDetached && this.quantityQueueEpoch === requestEpoch) {
        this.pendingQuantityChanges.clear();
        this.cartRecoveryPending = true;
        this.syncQuantityCheckout();
        await this.recoverInterruptedUpdate();
        if (!this.quantityQueueDetached && this.quantityQueueEpoch === requestEpoch) {
          const notice = this.querySelector("[data-cart-update-error]");
          if (notice) notice.hidden = false;
        }
      }
    });
  }
  async fetchCartData() {
    if (__privateGet(this, _pendingCartFetch)) return __privateGet(this, _pendingCartFetch);
    __privateSet(this, _pendingCartFetch, fetchCartJson("".concat(Theme.routes.cart_url, ".json"), {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    }, new AbortController(), 15e3).finally(() => {
      __privateSet(this, _pendingCartFetch, null);
    }));
    return __privateGet(this, _pendingCartFetch);
  }
  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error("Section id missing");
    return sectionId;
  }
  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== void 0;
  }
}
_debouncedOnChange = /* @__PURE__ */ new WeakMap();
_pendingCartFetch = /* @__PURE__ */ new WeakMap();
_activeQuantityRequest = /* @__PURE__ */ new WeakMap();
_CartItemsComponent_instances = /* @__PURE__ */ new WeakSet();
isExternalCartUpdate_fn = function(event) {
  return !(event.target instanceof Node) || !this.contains(event.target);
};
_handleDiscountUpdate = /* @__PURE__ */ new WeakMap();
_handleNoteUpdate = /* @__PURE__ */ new WeakMap();
onQuantityChange_fn = function(event) {
  if (!(event.target instanceof Node) || !this.contains(event.target)) return;
  const { quantity, cartLine: line } = event.detail;
  if (!line) return;
  if (quantity === 0) {
    return this.onLineItemRemove(line);
  }
  this.queueQuantityChange({
    line,
    quantity,
    action: "change"
  });
  const lineItemRow = this.refs.cartItemRows[line - 1];
  if (!lineItemRow) return;
  const textComponent = (
    /** @type {TextComponent | undefined} */
    lineItemRow.querySelector("text-component")
  );
  textComponent == null ? void 0 : textComponent.shimmer();
};
_handleCartError = /* @__PURE__ */ new WeakMap();
_handleCartUpdate = /* @__PURE__ */ new WeakMap();
disableCartItems_fn = function() {
  this.classList.add("cart-items-disabled");
};
enableCartItems_fn = function() {
  this.classList.remove("cart-items-disabled");
};
updateQuantitySelectors_fn = function(updatedCart) {
  if (!updatedCart.items) return;
  const quantities = /* @__PURE__ */ new Map();
  for (const item of updatedCart.items) {
    const variantId = item.variant_id.toString();
    quantities.set(variantId, (quantities.get(variantId) || 0) + item.quantity);
  }
  for (const selector of document.querySelectorAll("quantity-selector-component[data-variant-id]")) {
    const input = selector.querySelector("input[data-cart-quantity]");
    if (!input) continue;
    const quantity = quantities.get(selector.dataset.variantId) || 0;
    input.setAttribute("data-cart-quantity", quantity.toString());
    if ("updateCartQuantity" in selector && typeof selector.updateCartQuantity === "function") {
      selector.updateCartQuantity();
    }
  }
};
updateCartQuantitySelectorButtonStates_fn = function() {
  var _a;
  for (const selector of document.querySelectorAll("cart-quantity-selector-component")) {
    (_a = selector.updateButtonStates) == null ? void 0 : _a.call(selector);
  }
};
if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
export {
  CartItemsComponent
};
